// Banner de consentimento de cookies (LGPD) — granular.
//
// Aparece na 1ª visita ao SITE PÚBLICO (nunca no painel administrativo) enquanto
// não houver decisão registrada. A escolha é persistida em localStorage.
//
//  - "Aceitar todos"     → medição com cookie de visitante (recorrência).
//  - "Apenas essenciais" → medição anônima, sem cookie.
//  - "Personalizar"      → o visitante liga/desliga a categoria de Análise.

import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3 } from "lucide-react";
import { hasConsentDecision, setConsent } from "../../utils/consent";
import styles from "./CookieConsent.module.css";

export default function CookieConsent() {
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [showPrefs, setShowPrefs] = useState(false);
    const [analytics, setAnalytics] = useState(true);

    useEffect(() => {
        // Só exibe se ainda não houve decisão.
        if (!hasConsentDecision()) setVisible(true);
    }, []);

    // Nunca exibir na área administrativa.
    if (location.pathname.startsWith("/painel")) return null;
    if (!visible) return null;

    function decide(value) {
        setConsent(value);
        setVisible(false);
    }

    return (
        <div className={styles.banner} role="dialog" aria-live="polite" aria-label="Aviso de cookies">
            <div className={styles.main}>
                <div className={styles.icon} aria-hidden="true">
                    <Cookie size={22} />
                </div>
                <div className={styles.text}>
                    <strong>Nós usamos cookies</strong>
                    <p>
                        Utilizamos cookies para manter o site funcionando e para entender,
                        de forma anônima, como você navega. Você decide o que aceitar. Saiba mais na{" "}
                        <Link to="/politica-de-privacidade" className={styles.link}>
                            Política de Privacidade
                        </Link>
                        .
                    </p>
                </div>
            </div>

            {showPrefs && (
                <div className={styles.prefs}>
                    <div className={styles.prefRow}>
                        <div className={styles.prefInfo}>
                            <ShieldCheck size={17} aria-hidden="true" />
                            <div>
                                <strong>Essenciais</strong>
                                <span>Necessários para o funcionamento do site. Sempre ativos.</span>
                            </div>
                        </div>
                        <span className={`${styles.switch} ${styles.switchLocked}`} aria-hidden="true">
                            <span className={styles.knob} />
                        </span>
                    </div>

                    <div className={styles.prefRow}>
                        <div className={styles.prefInfo}>
                            <BarChart3 size={17} aria-hidden="true" />
                            <div>
                                <strong>Análise de acessos</strong>
                                <span>Mede acessos e reconhece visitas recorrentes (cookie hl_vid).</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={analytics}
                            aria-label="Ativar análise de acessos"
                            className={`${styles.switch} ${analytics ? styles.switchOn : ""}`}
                            onClick={() => setAnalytics((v) => !v)}
                        >
                            <span className={styles.knob} />
                        </button>
                    </div>
                </div>
            )}

            <div className={styles.actions}>
                {showPrefs ? (
                    <button
                        type="button"
                        className={styles.btnPrimary}
                        onClick={() => decide(analytics ? "all" : "essential")}
                    >
                        Salvar preferências
                    </button>
                ) : (
                    <>
                        <button
                            type="button"
                            className={styles.btnGhost}
                            onClick={() => setShowPrefs(true)}
                        >
                            Personalizar
                        </button>
                        <button
                            type="button"
                            className={styles.btnReject}
                            onClick={() => decide("essential")}
                        >
                            Apenas essenciais
                        </button>
                        <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={() => decide("all")}
                        >
                            Aceitar todos
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
